import { Fragment } from 'react';
import toCamelCase from '@grebban/utils/string/toCamelCase';
import { above } from '@sportson/core-web/utils/mediaqueries';
import styled from 'libs/styled';

import Text from 'components/Text';

export interface CSVData {
    rows: Record<string, string>[];
    headings?: string[];
    settings: any[];
}

export interface CSVContainer {
    csv: {
        csvData: CSVData;
        headerLine: boolean;
    };
}

export interface WordpressTableBlock {
    csvs: CSVContainer[];
    heading?: string;
    settings?: string[];
}

const Wrapper = styled('div')`
    width: 100%;
    overflow-x: auto;
    margin: 0 auto;

    ${above['tablet.lg']} {
        width: 58%;
    }
`;

const Table = styled('table')`
    width: 100%;
    border-collapse: collapse;
    margin-top: 20px;
`;

const Tr = styled('tr')`
    background-color: var(--color-brand-yellow);
`;

const Th = styled('th')`
    border: 1px solid var(--color-neutrals-200);
    text-align: left;
    padding: 16px;
`;

const Heading = styled(Th)`
    background-color: var(--color-brand-yellow);
`;

const SubHeading = styled(Th)`
    background-color: var(--color-neutrals-100);
`;

const Td = styled('td')`
    border: 1px solid var(--color-neutrals-200);
    text-align: left;
    padding: 16px;
    white-space: nowrap;

    ${above['tablet.lg']} {
        max-width: 200px;
        white-space: pre-line;
    }
`;

const TableSectionBlock = ({ csvs, heading = '', settings = [] }: WordpressTableBlock) => {
    const numberOfColumns = csvs.reduce((maxColumns, { csv }) => {
        const {
            csvData: { headings = [] },
        } = csv;
        return headings.length > maxColumns ? headings.length : maxColumns;
    }, 0);

    return (
        <Wrapper p={['40px 12px', null, null, null, '40px 40px 64px']}>
            <Table>
                {settings && settings.length > 0 && heading ? (
                    <thead>
                        <Tr>
                            <Heading colSpan={numberOfColumns}>
                                <Text typography="UI/20_100_0_450">{heading}</Text>
                            </Heading>
                        </Tr>
                    </thead>
                ) : null}
                {csvs.map(({ csv }, csvIndex) => {
                    const { csvData } = csv;
                    const { rows, headings } = csvData;
                    return (
                        <Fragment key={`csv-${csvIndex}`}>
                            {headings ? (
                                <thead>
                                    <tr>
                                        {headings.map((heading, headingIndex) => (
                                            <SubHeading key={`${heading}-${headingIndex}`}>
                                                <Text typography="UI/12_100_0_450_uppercase">{heading}</Text>
                                            </SubHeading>
                                        ))}
                                    </tr>
                                </thead>
                            ) : null}
                            <tbody>
                                {rows.map((row, rowIndex) => (
                                    <tr key={`row-${rowIndex}`}>
                                        {headings?.map((heading, headingIndex) => {
                                            const headingKey =
                                                heading === '' ? headingIndex.toString() : toCamelCase(heading);
                                            return (
                                                <Td key={`${rowIndex}-${heading}-${headingIndex}`}>
                                                    <Text typography="Content/14_130_0_350">{row[headingKey]}</Text>
                                                </Td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </Fragment>
                    );
                })}
            </Table>
        </Wrapper>
    );
};

export default TableSectionBlock;
